// OnHandDrawer — replaces SkuDetailPane.jsx for the On-Hand context.
//
// Hero block: Stock Updater (Was → New + steppers + reason chips +
// optional supplier override + note), writes through writeStockAdjustment
// in stockWrites.js so the typed audit ledger stays the source of truth.
//
// 3 sub-tabs:
//   Incoming — read-only POs for this SKU (forecaster_incoming_stock)
//   Sales    — last 14 weeks chart + monthly_avg_override editor +
//              anomaly tag inline editor (folded in from old SkuPane.jsx)
//   History  — forecaster_stock_adjustments rows + filtered
//              forecaster_activity rows, merged into one timeline
//
// Catalogue-only fields (cost/lead/MOQ/category/supplier) sit in a
// "Locked summary" block with a "Edit in Catalogue →" link — those
// are owned by the Catalogue drawer (PR-6).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { writeStockAdjustment } from './stockWrites';

const SERIF = '\'Tiempos\', Georgia, \'Times New Roman\', serif';
const SANS  = 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif';
const MONO  = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

const REASONS = [
  { id: 'count',      label: 'Stock count',       hint: 'Routine recount' },
  { id: 'received',   label: 'Received delivery', hint: 'PO arrived' },
  { id: 'damaged',    label: 'Damaged / expired', hint: 'Write-off' },
  { id: 'correction', label: 'Correction',        hint: 'Fix an error' },
  { id: 'transfer',   label: 'Transferred out',   hint: 'To another location' },
];

function fmtMoney(n) { return `A$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }
function fmtShortDate(ts) { if (!ts) return ''; return new Date(ts).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }); }
function fmtRelative(ts) {
  if (!ts) return '';
  const ms = Date.now() - new Date(ts).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m || '<1'}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 14) return `${d}d ago`;
  return fmtShortDate(ts);
}

function coverTone(d) {
  if (d == null) return '#6E7278';
  if (d < 14) return '#C2410C';
  if (d < 30) return '#B05610';
  if (d < 60) return '#0F8E6F';
  return '#16A34A';
}

export default function OnHandDrawer({
  session, sku, catalog = [], weeklyData = [], incomingData = [],
  onClose, onSaved,
}) {
  const [tab, setTab] = useState('incoming');
  const [newOnHand, setNewOnHand] = useState(sku?.stock_on_hand ?? 0);
  const [reason, setReason] = useState('count');
  const [note, setNote] = useState('');
  const [supplier, setSupplier] = useState('');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const cat = useMemo(
    () => sku?.catalog_id ? (catalog.find(c => c.id === sku.catalog_id) || null) : null,
    [sku, catalog],
  );

  // Reset form when the drawer opens for a new SKU.
  useEffect(() => {
    if (!sku) return;
    setTab('incoming');
    setNewOnHand(sku.stock_on_hand ?? 0);
    setReason('count');
    setNote('');
    setSupplier(cat?.supplier_name || '');
    setJustSaved(false);
  }, [sku?.id, cat?.supplier_name]);

  // Esc to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!sku) return null;

  const monthlyAvg = Number(sku.monthly_avg_override ?? sku.monthly_sales_avg ?? 0);
  const dailyAvg = monthlyAvg / 30;
  const currentDays = dailyAvg > 0 ? Math.round(Number(sku.stock_on_hand || 0) / dailyAvg) : null;
  const skuIncoming = useMemo(() => incomingData.filter(i => i.sku_id === sku.id), [incomingData, sku.id]);
  const incomingQty = skuIncoming.reduce((a, b) => a + Number(b.qty || 0), 0);

  const newOnHandNum = parseInt(newOnHand) || 0;
  const delta = newOnHandNum - Number(sku.stock_on_hand || 0);
  const supplierChanged = !!cat && (supplier || '') !== (cat.supplier_name || '');
  const dirty = delta !== 0 || (reason === 'received' && supplierChanged);
  const nextDays = dailyAvg > 0 ? Math.round(newOnHandNum / dailyAvg) : null;

  // Functional setState so rapid taps don't stale-read newOnHand.
  const adjust = (n) => setNewOnHand(prev => Math.max(0, (parseInt(prev) || 0) + n));

  async function handleSave() {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      // Optional supplier sync on receive.
      if (reason === 'received' && supplierChanged && cat) {
        await authWrite(session, 'forecaster_catalog', 'update',
          { supplier_name: supplier || null },
          { column: 'id', value: cat.id });
      }
      // Single source of truth: writeStockAdjustment bumps SOH + writes
      // typed ledger row + emits the free-text activity mirror.
      if (delta !== 0) {
        await writeStockAdjustment(session, {
          skuId: sku.id,
          skuName: sku.sku_name,
          delta,
          newOnhand: newOnHandNum,
          reason,
          note: note.trim() || null,
          supplierName: reason === 'received' ? (supplier || null) : null,
        });
      }
      setJustSaved(true);
      onSaved?.();
      setTimeout(() => setJustSaved(false), 1400);
    } catch (err) {
      console.error('[OnHandDrawer] save failed', err);
      alert('Save failed: ' + (err?.message || 'unknown error'));
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(15,20,25,0.32)', backdropFilter: 'blur(2px)',
      }} />
      <aside style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 100vw)',
        background: '#FAFAF8', zIndex: 1000,
        display: 'flex', flexDirection: 'column', fontFamily: SANS,
        borderLeft: '1px solid #E8E6E0', overflow: 'hidden',
      }}>
        <DrawerHeader sku={sku} cat={cat} onClose={onClose} />

        <div style={{ overflowY: 'auto', flex: 1 }}>
          <StockUpdater
            sku={sku}
            cat={cat}
            currentDays={currentDays}
            nextDays={nextDays}
            newOnHand={newOnHand}
            setNewOnHand={setNewOnHand}
            adjust={adjust}
            delta={delta}
            reason={reason}
            setReason={setReason}
            note={note}
            setNote={setNote}
            supplier={supplier}
            setSupplier={setSupplier}
            dirty={dirty}
            saving={saving}
            justSaved={justSaved}
            onSave={handleSave}
            incomingQty={incomingQty}
            dailyAvg={dailyAvg}
          />

          {cat && <LockedSummary cat={cat} />}

          {/* sub-tabs — Incoming / Sales / History. Pulled into a
              clearer pill row so they don't get lost beneath the
              stock-updater editor; History especially is critical
              per Mo's "every SKU mention shows its history" rule. */}
          <div style={{
            display: 'flex', gap: 6, padding: '12px 26px 0',
            marginTop: 8,
          }}>
            {[
              { id: 'incoming', label: 'Incoming', count: skuIncoming.length || null },
              { id: 'sales',    label: 'Sales' },
              { id: 'history',  label: 'History' },
            ].map(t => {
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} style={{
                  padding: '7px 14px',
                  background: active ? '#0F1419' : '#fff',
                  color:       active ? '#F4EFE6' : '#0F1419',
                  border: '1px solid ' + (active ? '#0F1419' : '#E8E6E0'),
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontSize: 12.5, fontWeight: 500,
                  letterSpacing: '-0.005em',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  transition: 'background 140ms, color 140ms, border-color 140ms',
                }}>
                  <span>{t.label}</span>
                  {t.count != null && (
                    <span style={{
                      fontFamily: MONO, fontSize: 10,
                      color: active ? 'rgba(244,239,230,0.6)' : '#9A9DA1',
                      padding: '0 6px',
                      borderRadius: 999,
                      background: active ? 'rgba(244,239,230,0.12)' : '#F4F2EE',
                    }}>{t.count}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div style={{
            margin: '8px 26px 0',
            borderBottom: '1px solid #E8E6E0',
          }} />

          <div style={{ padding: '18px 26px 26px' }}>
            {tab === 'incoming' && <IncomingSubTab incoming={skuIncoming} />}
            {tab === 'sales' && (
              <SalesSubTab
                session={session}
                sku={sku}
                weeklyData={weeklyData}
                onSaved={onSaved}
              />
            )}
            {tab === 'history' && <HistorySubTab sku={sku} cat={cat} />}
          </div>
        </div>
      </aside>
    </>,
    document.body,
  );
}

function DrawerHeader({ sku, cat, onClose }) {
  return (
    <div style={{
      padding: '20px 26px 14px', borderBottom: '1px solid #E8E6E0',
      display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h2 style={{
          fontFamily: SERIF, fontSize: 22, fontWeight: 400,
          color: '#0F1419', margin: 0, letterSpacing: '-0.01em',
          textWrap: 'pretty',
        }}>{cat?.sku_name || sku.sku_name}</h2>
        <div style={{
          fontFamily: MONO, fontSize: 11, color: '#6E7278',
          marginTop: 4, display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <span>{cat?.sku_code || '—'}</span>
          <span style={{ color: '#D5D3CD' }}>·</span>
          <span>{cat?.category || '—'}</span>
          <span style={{ color: '#D5D3CD' }}>·</span>
          <span style={{
            color: cat?.status === 'retired' ? '#9A9DA1' : '#0F8E6F',
            fontWeight: 500,
          }}>{cat?.status === 'retired' ? 'Retired' : 'Active'}</span>
        </div>
      </div>
      <button onClick={onClose} aria-label="Close" style={{
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: '#6E7278', padding: 6, borderRadius: 6, lineHeight: 1,
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

function StockUpdater({
  sku, cat, currentDays, nextDays,
  newOnHand, setNewOnHand, adjust, delta,
  reason, setReason, note, setNote, supplier, setSupplier,
  dirty, saving, justSaved, onSave, incomingQty, dailyAvg,
}) {
  const newOnHandNum = parseInt(newOnHand) || 0;
  const tone = coverTone(nextDays);
  const dailyDisplay = dailyAvg > 0 ? `${Math.round(dailyAvg * 10) / 10}/d burn` : 'No sales';

  return (
    <div style={{ padding: '22px 26px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 18 }}>
        <span style={labelStyle}>Current on hand</span>
        <span style={{ flex: 1 }} />
        <span style={{
          fontFamily: MONO, fontSize: 10, color: '#6E7278',
          letterSpacing: '0.08em',
        }}>
          {dailyDisplay}
          {incomingQty > 0 && <> · +{incomingQty.toLocaleString()} inbound</>}
        </span>
      </div>

      {/* Was → New display */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center',
        gap: 12, marginBottom: 16,
        padding: '16px 18px', borderRadius: 12,
        background: '#fff', border: '1px solid #E8E6E0',
      }}>
        <div>
          <div style={miniLabelStyle}>Was</div>
          <div style={{
            fontFamily: SERIF, fontSize: 28, fontWeight: 400,
            color: '#9A9DA1', letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums', lineHeight: 1,
          }}>{Number(sku.stock_on_hand || 0).toLocaleString()}</div>
          <div style={{ fontSize: 11, color: '#6E7278', marginTop: 4 }}>
            {currentDays != null ? `${currentDays}d cover` : '—'}
          </div>
        </div>
        <svg width="18" height="18" viewBox="0 0 18 18" style={{ color: '#9A9DA1' }}>
          <path d="M4 9H14M14 9L10 5M14 9L10 13" stroke="currentColor" strokeWidth="1.5"
            fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div>
          <div style={miniLabelStyle}>New</div>
          <div style={{
            fontFamily: SERIF, fontSize: 28, fontWeight: 400,
            color: delta === 0 ? '#0F1419'
              : delta > 0 ? '#0F8E6F' : '#C2410C',
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums', lineHeight: 1,
          }}>{newOnHandNum.toLocaleString()}</div>
          <div style={{
            fontSize: 11, marginTop: 4,
            color: delta === 0 ? '#6E7278' : tone,
            fontWeight: delta !== 0 ? 500 : 400,
          }}>
            {delta === 0 ? 'No change' : (
              <>
                {delta > 0 ? '+' : ''}{delta.toLocaleString()}
                {nextDays != null && <> · {nextDays}d cover</>}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Steppers */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {[-100, -10, -1, +1, +10, +100].map(n => (
          <button key={n} onClick={() => adjust(n)} style={{
            flex: 1, minWidth: 44,
            padding: '8px 0', borderRadius: 7,
            border: '1px solid #E8E6E0', background: '#fff', cursor: 'pointer',
            fontFamily: MONO, fontSize: 12,
            color: n < 0 ? '#C2410C' : '#0F8E6F',
            fontWeight: 500, letterSpacing: '0.02em',
            fontVariantNumeric: 'tabular-nums',
          }}>{n > 0 ? '+' : ''}{n}</button>
        ))}
      </div>

      {/* Exact entry */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <span style={{ ...labelStyle, width: 78, flexShrink: 0 }}>Exact</span>
        <input
          type="number" min={0}
          value={newOnHand}
          onChange={(e) => setNewOnHand(e.target.value)}
          style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
        />
        <button onClick={() => setNewOnHand(sku.stock_on_hand ?? 0)} style={{
          padding: '8px 12px', borderRadius: 7, flexShrink: 0,
          border: '1px solid #E8E6E0', background: '#fff',
          fontSize: 11.5, color: '#6E7278', cursor: 'pointer',
          fontFamily: MONO, letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>Reset</button>
      </div>

      {/* Reason chips */}
      <div style={{ ...labelStyle, marginBottom: 8 }}>Reason</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {REASONS.map(r => (
          <button key={r.id} onClick={() => setReason(r.id)} style={{
            padding: '7px 11px', borderRadius: 999,
            border: reason === r.id ? '1px solid #0F1419' : '1px solid #E8E6E0',
            background: reason === r.id ? '#0F1419' : '#fff',
            color: reason === r.id ? '#fff' : '#0F1419',
            fontSize: 12, cursor: 'pointer',
            letterSpacing: '-0.005em',
          }}>{r.label}</button>
        ))}
      </div>

      {/* Supplier appears only when reason='received' */}
      {reason === 'received' && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ ...labelStyle, marginBottom: 6 }}>Supplier</div>
          <input
            value={supplier}
            placeholder="e.g. Melbourne Co-Pack"
            onChange={(e) => setSupplier(e.target.value)}
            style={inputStyle}
          />
          {cat && supplier !== (cat.supplier_name || '') && (
            <div style={{ fontSize: 10.5, color: '#B05610', marginTop: 4 }}>
              Will update catalogue supplier from "{cat.supplier_name || '—'}"
            </div>
          )}
        </div>
      )}

      {/* Note */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ ...labelStyle, marginBottom: 6 }}>
          Note <span style={{
            fontFamily: 'inherit', textTransform: 'none',
            letterSpacing: 'normal', color: '#6E7278', fontWeight: 400,
          }}>— optional</span>
        </div>
        <input
          value={note}
          placeholder="Anything worth logging?"
          onChange={(e) => setNote(e.target.value)}
          style={inputStyle}
        />
      </div>

      {/* Save */}
      <button
        disabled={!dirty || saving}
        onClick={onSave}
        style={{
          width: '100%', padding: '12px 16px', borderRadius: 9,
          background: justSaved ? '#0F8E6F'
            : dirty ? '#0F1419' : 'rgba(15,20,25,0.08)',
          color: justSaved || dirty ? '#fff' : '#9A9DA1',
          border: 'none', cursor: dirty && !saving ? 'pointer' : 'default',
          fontSize: 13.5, fontWeight: 500, letterSpacing: '-0.005em',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'background 0.2s, color 0.2s',
        }}
      >
        {justSaved ? (
          <>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7L6 11L12 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Stock updated
          </>
        ) : saving ? 'Saving…' : dirty ? (
          <>
            Update stock
            <span style={{ opacity: 0.7, fontFamily: MONO, fontSize: 11 }}>
              {Number(sku.stock_on_hand || 0).toLocaleString()} → {newOnHandNum.toLocaleString()}
            </span>
          </>
        ) : 'No changes yet'}
      </button>
    </div>
  );
}

function LockedSummary({ cat }) {
  const rows = [
    { label: 'Unit cost',   value: cat.unit_cost != null ? fmtMoney(cat.unit_cost) : '—' },
    { label: 'Lead time',   value: `${cat.lead_time_days || 84}d` },
    { label: 'Batch / MOQ', value: Number(cat.batch_size || 0).toLocaleString() },
    { label: 'Category',    value: cat.category || '—' },
    { label: 'Supplier',    value: cat.supplier_name || '—' },
    { label: 'List price',  value: cat.list_price != null ? fmtMoney(cat.list_price) : '—' },
  ];
  return (
    <div style={{
      margin: '0 26px 18px',
      padding: '14px 16px', borderRadius: 10,
      background: 'rgba(15,20,25,0.025)',
      border: '1px dashed #E8E6E0',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <svg width="11" height="11" viewBox="0 0 11 11" style={{ color: '#9A9DA1' }}>
          <path d="M3 5V3.5a2.5 2.5 0 0 1 5 0V5" stroke="currentColor" strokeWidth="1.2" fill="none" />
          <rect x="2" y="5" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" fill="none" />
        </svg>
        <span style={{ ...labelStyle, margin: 0 }}>Catalogue details</span>
        <span style={{ flex: 1 }} />
        <span style={{
          fontFamily: MONO, fontSize: 10, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: '#0F8E6F', fontWeight: 500,
        }}>
          Edit in Catalogue →
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 14px' }}>
        {rows.map(r => (
          <div key={r.label}>
            <div style={{
              fontFamily: MONO, fontSize: 8.5, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: '#9A9DA1', marginBottom: 2,
            }}>{r.label}</div>
            <div style={{
              fontSize: 12.5, color: '#0F1419',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{r.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Sub-tab: Incoming ──────────────────────────────────────────────────
function IncomingSubTab({ incoming }) {
  if (incoming.length === 0) {
    return <div style={emptyStyle}>No incoming POs for this SKU.</div>;
  }
  const sorted = [...incoming].sort((a, b) => a.expected_date.localeCompare(b.expected_date));

  // Batches subsection: any incoming row with batch / expiry surfaces here,
  // sorted by expiry asc (next-to-expire first). Per Ace's audit
  // (2026-04-27) — operators need this view to plan rotation. Receipts
  // that haven't been received yet still show because expiry is set
  // at receipt and batch is recorded at PO time too.
  const withBatch = sorted.filter(po => po.batch_number || po.expiry_date);
  const sortedBatches = withBatch
    .slice()
    .sort((a, b) => (a.expiry_date || '9999').localeCompare(b.expiry_date || '9999'));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {sortedBatches.length > 0 && (
        <div>
          <div style={{
            fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: '#6E7278', marginBottom: 8,
          }}>
            Batches in stock <span style={{ color: '#9A9DA1' }}>· {sortedBatches.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sortedBatches.map(b => {
              const expMs = b.expiry_date ? new Date(b.expiry_date).getTime() - Date.now() : null;
              const expDays = expMs != null ? Math.ceil(expMs / 86400000) : null;
              const expiringSoon = expDays != null && expDays >= 0 && expDays < 60;
              const expired = expDays != null && expDays < 0;
              return (
                <div key={b.id} style={{
                  padding: '10px 14px', background: expired ? '#FBE9E5' : expiringSoon ? '#FBF1E6' : '#FAFAF8',
                  border: `1px solid ${expired ? '#E8C8C0' : expiringSoon ? '#EBDFCD' : '#EFEDE7'}`,
                  borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{ fontFamily: MONO, fontSize: 11, color: '#0F1419', fontWeight: 500 }}>
                      {b.batch_number || '(no batch)'}
                    </div>
                    <div style={{ fontSize: 11, color: '#6E7278', marginTop: 2 }}>
                      {Number(b.qty || 0).toLocaleString()} units
                      {b.warehouse_location && <> · {b.warehouse_location}</>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {b.expiry_date ? (
                      <>
                        <div style={{ fontSize: 12, color: '#0F1419' }}>exp {fmtShortDate(b.expiry_date)}</div>
                        <div style={{
                          fontFamily: MONO, fontSize: 10,
                          color: expired ? '#A86A2E' : expiringSoon ? '#A86A2E' : '#9A9DA1',
                          textTransform: 'uppercase', letterSpacing: '0.12em',
                        }}>
                          {expired ? 'EXPIRED' : expiringSoon ? `in ${expDays}d` : `in ${expDays}d`}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 11, color: '#9A9DA1' }}>no expiry</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <div style={{
          fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: '#6E7278', marginBottom: 8,
        }}>
          POs <span style={{ color: '#9A9DA1' }}>· {sorted.length}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sorted.map(po => {
            const etaMs = new Date(po.expected_date).getTime() - Date.now();
            const etaDays = Math.max(0, Math.ceil(etaMs / 86400000));
            return (
              <div key={po.id} style={{
                padding: '14px 16px', background: '#fff',
                border: '1px solid #E8E6E0', borderRadius: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ fontFamily: MONO, fontSize: 11, color: '#6E7278' }}>{po.po_ref || '—'}</div>
                  <div style={{ fontSize: 13, color: '#0F1419', marginTop: 3 }}>
                    {Number(po.qty || 0).toLocaleString()} units
                    {po.warehouse_location && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: '#6E7278' }}>· {po.warehouse_location}</span>
                    )}
                  </div>
                  {(po.batch_number || po.skutopia_transfer_id) && (
                    <div style={{ fontSize: 11, color: '#6E7278', marginTop: 2 }}>
                      {po.batch_number && <>batch {po.batch_number}</>}
                      {po.batch_number && po.skutopia_transfer_id && <> · </>}
                      {po.skutopia_transfer_id && <>Skutopia {po.skutopia_transfer_id}</>}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, color: '#0F1419' }}>{fmtShortDate(po.expected_date)}</div>
                  <div style={{
                    fontFamily: MONO, fontSize: 10, color: '#9A9DA1',
                    textTransform: 'uppercase', letterSpacing: '0.12em',
                  }}>in {etaDays}d</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Sub-tab: Sales ─────────────────────────────────────────────────────
function SalesSubTab({ session, sku, weeklyData, onSaved }) {
  const [tags, setTags] = useState([]);
  const [editingWeek, setEditingWeek] = useState(null);
  const [tagInput, setTagInput] = useState('');

  const loadTags = useCallback(async () => {
    if (!sku?.id) return;
    const { data } = await aws.from('forecaster_anomaly_tags').select('*').eq('sku_id', sku.id);
    setTags(data || []);
  }, [sku?.id]);

  useEffect(() => { loadTags(); }, [loadTags]);

  const last14 = useMemo(() => {
    const rows = weeklyData
      .filter(w => w.sku_id === sku.id)
      .sort((a, b) => a.week_start.localeCompare(b.week_start));
    return rows.slice(-14);
  }, [weeklyData, sku.id]);

  const monthlyAvg = Number(sku.monthly_avg_override ?? sku.monthly_sales_avg ?? 0);
  const dailyAvg = monthlyAvg / 30;
  const weekAvg = dailyAvg * 7;
  const max = Math.max(1, ...last14.map(w => Number(w.units_sold || 0)));

  async function setOverride() {
    const val = prompt('Monthly average override (blank to clear):', sku.monthly_avg_override ?? '');
    if (val === null) return;
    if (val.trim() === '') {
      await authWrite(session, 'forecaster_skus', 'update',
        { monthly_avg_override: null, override_reason: null, updated_by: session.name },
        { column: 'id', value: sku.id });
      await aws.from('forecaster_activity').insert([{
        actor_name: session.name, action: 'Cleared override', detail: sku.sku_name,
      }]);
    } else {
      const reason = prompt('Reason for override:');
      if (!reason?.trim()) return;
      await authWrite(session, 'forecaster_skus', 'update',
        { monthly_avg_override: parseInt(val) || 0, override_reason: reason, updated_by: session.name },
        { column: 'id', value: sku.id });
      await aws.from('forecaster_activity').insert([{
        actor_name: session.name, action: 'Set override',
        detail: `${sku.sku_name}: ${val}/mo (${reason})`,
      }]);
    }
    onSaved?.();
  }

  async function saveTag(weekStart) {
    const text = tagInput.trim();
    if (text) {
      await authWrite(session, 'forecaster_anomaly_tags', 'upsert', {
        sku_id: sku.id, week_start: weekStart, tag_text: text, created_by: session.name,
      }, null, 'sku_id,week_start');
    } else {
      // Composite-key delete via the column2/value2 extension to doDelete.
      await authWrite(session, 'forecaster_anomaly_tags', 'delete', null, {
        column: 'sku_id', value: sku.id,
        column2: 'week_start', value2: weekStart,
      });
    }
    setEditingWeek(null);
    setTagInput('');
    loadTags();
  }

  return (
    <div>
      {/* run-rate KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
        {[
          { l: 'Run rate', v: dailyAvg > 0 ? `${Math.round(dailyAvg * 10) / 10}/d` : '—' },
          { l: 'Week',     v: weekAvg > 0 ? Math.round(weekAvg).toLocaleString() : '—' },
          { l: 'Month',    v: monthlyAvg > 0 ? monthlyAvg.toLocaleString() : '—' },
        ].map(t => (
          <div key={t.l} style={{
            padding: '11px 12px', background: '#fff',
            border: '1px solid #E8E6E0', borderRadius: 8,
          }}>
            <div style={miniLabelStyle}>{t.l}</div>
            <div style={{
              fontSize: 17, fontWeight: 500, letterSpacing: '-0.015em',
              color: '#0F1419', fontVariantNumeric: 'tabular-nums', marginTop: 3,
            }}>{t.v}</div>
          </div>
        ))}
      </div>

      {/* Override */}
      <div style={{
        padding: '12px 14px', background: '#fff', border: '1px solid #E8E6E0',
        borderRadius: 8, marginBottom: 14, display: 'flex',
        alignItems: 'center', gap: 12,
      }}>
        <div style={{ flex: 1 }}>
          <div style={miniLabelStyle}>Monthly avg override</div>
          <div style={{ fontSize: 13, color: '#0F1419', marginTop: 2 }}>
            {sku.monthly_avg_override != null
              ? `${sku.monthly_avg_override}/mo`
              : `${Math.round(Number(sku.monthly_sales_avg || 0)).toLocaleString()}/mo (derived)`}
          </div>
          {sku.override_reason && (
            <div style={{ fontSize: 11, color: '#6E7278', marginTop: 2 }}>
              Reason: {sku.override_reason}
            </div>
          )}
        </div>
        <button onClick={setOverride} style={{
          padding: '6px 12px', borderRadius: 6,
          border: '1px solid #E8E6E0', background: '#fff',
          fontSize: 12, color: '#0F1419', cursor: 'pointer',
        }}>{sku.monthly_avg_override != null ? 'Change' : 'Set override'}</button>
      </div>

      {/* Velocity chart with anomaly tags */}
      <div style={{
        padding: 16, border: '1px solid #E8E6E0', borderRadius: 10, background: '#fff',
      }}>
        <div style={{ ...labelStyle, marginBottom: 10 }}>Velocity · last {last14.length} weeks</div>
        {last14.length === 0 ? (
          <div style={emptyStyle}>No weekly sales data.</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 100 }}>
            {last14.map(w => {
              const v = Number(w.units_sold || 0);
              const h = Math.max(2, (v / max) * 88);
              const tag = tags.find(t => t.week_start === w.week_start);
              const isEditing = editingWeek === w.week_start;
              return (
                <div key={w.week_start} style={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', gap: 2, position: 'relative',
                  minWidth: 0,
                }}>
                  <div style={{
                    width: '100%', height: h, background: tag ? '#B05610' : '#0F8E6F',
                    borderRadius: '3px 3px 0 0', cursor: 'pointer',
                  }}
                    onClick={() => {
                      setEditingWeek(w.week_start);
                      setTagInput(tag?.tag_text || '');
                    }}
                    title={tag ? `${tag.tag_text} (click to edit)` : 'Click to tag'}
                  />
                  <div style={{
                    fontFamily: MONO, fontSize: 8, color: '#9A9DA1',
                    transform: 'rotate(-30deg)', transformOrigin: 'center',
                    whiteSpace: 'nowrap',
                  }}>{w.week_start.slice(5)}</div>
                  {isEditing && (
                    <div style={{
                      position: 'absolute', bottom: 30, left: '50%',
                      transform: 'translateX(-50%)', zIndex: 5,
                      background: '#fff', border: '1px solid #D5D3CD',
                      borderRadius: 6, padding: 6, display: 'flex', gap: 4,
                      width: 200,
                    }}>
                      <input
                        value={tagInput}
                        onChange={e => setTagInput(e.target.value)}
                        placeholder="Tag (e.g. promo)"
                        autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') saveTag(w.week_start); if (e.key === 'Escape') setEditingWeek(null); }}
                        style={{ flex: 1, fontSize: 12, border: '1px solid #E8E6E0', padding: '4px 6px', borderRadius: 4 }}
                      />
                      <button onClick={() => saveTag(w.week_start)} style={{
                        fontSize: 11, padding: '4px 8px', borderRadius: 4,
                        background: '#0F1419', color: '#fff', border: 'none', cursor: 'pointer',
                      }}>OK</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div style={{ fontSize: 10.5, color: '#9A9DA1', marginTop: 8 }}>
          Click a bar to add or edit an anomaly tag (e.g. "promo week", "stockout").
        </div>
      </div>
    </div>
  );
}

// ── Sub-tab: History ───────────────────────────────────────────────────
function HistorySubTab({ sku, cat }) {
  const [adjustments, setAdjustments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      const [adjRes, actRes] = await Promise.all([
        aws.from('forecaster_stock_adjustments')
          .select('*').eq('sku_id', sku.id)
          .order('created_at', { ascending: false }).limit(50),
        aws.from('forecaster_activity')
          .select('*')
          .ilike('detail', `%${sku.sku_name}%`)
          .order('created_at', { ascending: false }).limit(50),
      ]);
      if (!alive) return;
      setAdjustments(adjRes.data || []);
      setActivity(actRes.data || []);
      setLoading(false);
    }
    load();
    return () => { alive = false; };
  }, [sku.id, sku.sku_name]);

  const merged = useMemo(() => {
    const a = adjustments.map(r => ({
      kind: 'adjustment',
      key: 'adj-' + r.id,
      ts: r.created_at,
      who: r.actor_name,
      reason: r.reason,
      delta: r.delta,
      newOnhand: r.new_onhand,
      note: r.note,
      poRef: r.po_ref,
    }));
    const seenAdjustmentTs = new Set(a.map(x => x.ts?.slice(0, 16)));
    const b = activity
      // Filter out the free-text mirror rows we wrote — they duplicate the typed adjustment.
      .filter(r => !seenAdjustmentTs.has(r.created_at?.slice(0, 16)) || r.action !== 'Stock adjusted')
      .map(r => ({
        kind: 'activity',
        key: 'act-' + r.id,
        ts: r.created_at,
        who: r.actor_name,
        action: r.action,
        detail: r.detail,
      }));
    // Synthetic "Created" row from the catalogue entry — anchors the
    // history at the bottom even before any stock movement happens
    // (migration 102 added created_by; created_at has always been there).
    const out = [...a, ...b];
    if (cat?.created_at) {
      out.push({
        kind: 'created',
        key: 'cat-created-' + (cat.id || 'x'),
        ts: cat.created_at,
        who: cat.created_by || null,
      });
    }
    return out.sort((x, y) => (y.ts || '').localeCompare(x.ts || ''));
  }, [adjustments, activity, cat]);

  if (loading) return <div style={emptyStyle}>Loading…</div>;
  if (merged.length === 0) return <div style={emptyStyle}>No history yet.</div>;

  const dot = {
    count: '#0F8E6F', received: '#16A34A',
    damaged: '#DC2626', correction: '#D97706', transfer: '#6E7278',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {merged.map(r => {
        const dotColor =
          r.kind === 'adjustment' ? (dot[r.reason] || '#6E7278')
          : r.kind === 'created'   ? '#0F8E6F'
          : '#9A9DA1';
        return (
          <div key={r.key} style={{ display: 'flex', gap: 12, fontSize: 13 }}>
            <div style={{
              width: 8, height: 8, borderRadius: 999,
              background: dotColor,
              marginTop: 5, flexShrink: 0,
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: '#0F1419' }}>
                {r.kind === 'adjustment' ? (
                  <>
                    <strong style={{ fontWeight: 500, textTransform: 'capitalize' }}>{r.reason}</strong>
                    {' · '}
                    <span style={{ fontFamily: MONO }}>
                      {r.delta > 0 ? '+' : ''}{r.delta} → {r.newOnhand}
                    </span>
                    {r.poRef && <> · <span style={{ fontFamily: MONO, color: '#6E7278' }}>{r.poRef}</span></>}
                    {r.note && <span style={{ color: '#6E7278' }}> · {r.note}</span>}
                  </>
                ) : r.kind === 'created' ? (
                  <strong style={{ fontWeight: 500 }}>SKU created</strong>
                ) : (
                  <>{r.action}{r.detail && <span style={{ color: '#6E7278' }}> · {r.detail}</span>}</>
                )}
              </div>
              <div style={{
                fontFamily: MONO, fontSize: 10, color: '#9A9DA1',
                letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2,
              }}>
                {fmtRelative(r.ts)} · {r.who || 'system'}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Shared inline-style snippets ────────────────────────────────────────
const labelStyle = {
  fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.14em',
  textTransform: 'uppercase', color: '#6E7278', fontWeight: 500,
};
const miniLabelStyle = {
  fontFamily: MONO, fontSize: 9, letterSpacing: '0.14em',
  textTransform: 'uppercase', color: '#9A9DA1', marginBottom: 4,
};
const inputStyle = {
  flex: 1, padding: '8px 12px',
  border: '1px solid #E8E6E0', borderRadius: 7,
  background: '#fff', fontSize: 13, color: '#0F1419',
  fontFamily: 'inherit', outline: 'none',
};
const emptyStyle = {
  color: '#9A9DA1', fontSize: 13, padding: '20px 0', textAlign: 'center',
};
